import { ApiError, api } from '../../lib/api'
import { dataParams, readDataTable } from '../../lib/dataTable'
import { download } from '../../lib/download'
import { workbookToCsv } from '../../lib/spreadsheet'
import { upload } from '../../lib/upload'
import { isEnglish } from '../../lib/i18n'

/**
 * Users — `Features/Admin/UsersManagement/Endpoint.cs`.
 *
 *   getUsersPage -> GET  /api/users            -> UsersPageViewModel
 *   listUsers    -> POST /Users/Filter         { DataParams, Type, GroupId, IsAdmin, … }
 *                                              -> DataTables { Draw, RecordsTotal, RecordsFiltered, Data }
 *   exportUsers  -> GET  /Users/Export         ?tableType=&type=&filter=   -> a file
 *
 * THIS SLICE IS THE REFERENCE for every later list, and it is where the §3.1 adapters
 * first earn their keep. Three things here recur across the product:
 *
 * 1. **The DataTables contract.** `/Users/Filter` binds `[FromBody]`, so the request is
 *    built with `dataParams()` — the nested `DataParams` object — while other lists bind
 *    a form and use `dataParamsForm()`. Read the endpoint before writing the service.
 *
 * 2. **The rows are pre-rendered for the page they were written for.** `Link` is not a
 *    URL; it is a fragment of HTML:
 *
 *        <a href='/UserDetailsAdmin?username=bakr'>bakr</a>
 *
 *    The username is READ OUT of it and never injected. A username can contain markup —
 *    people choose their own at signup — and rendering that string would put an
 *    injection point in the middle of the users table.
 *
 * 3. **Server-side paging.** The mock filtered and counted in the browser; the endpoint
 *    does both. The screen passes its page, sort and search down instead of receiving
 *    everything at once.
 */

/** `<a href='/UserDetailsAdmin?username=x'>x</a>` -> `x`. Parsed, never rendered. */
function usernameFromLink(link) {
  if (!link) return ''

  const fromHref = /username=([^'"&]+)/i.exec(link)
  if (fromHref) {
    try {
      return decodeURIComponent(fromHref[1])
    } catch {
      return fromHref[1]
    }
  }

  // No href to read: fall back to the anchor's text, with any markup stripped.
  return link.replace(/<[^>]*>/g, '').trim()
}

/** The five colours the handler emits, mapped onto the shared kit's tones. */
const BADGE_TONES = {
  success: 'success',
  danger: 'danger',
  warning: 'warning',
  info: 'info',
  primary: 'primary',
  dark: 'dark',
  secondary: 'dark',
}

function toRow(dto) {
  const userName = usernameFromLink(dto.Link)
  const name = (isEnglish ? dto.EnglishName : dto.ArabicName) || dto.ArabicName || dto.EnglishName || userName

  return {
    // The account name is the key everywhere else in this slice, and the detail route.
    id: userName,
    userName,
    name,
    email: dto.Email ?? '',
    /* The handler returns the user's FIRST department only — `Groups.Where(TypeId == 1)
       .FirstOrDefault()` — so the column shows one chip. See API_FINDINGS.md §20. */
    groupNames: dto.Group ? [dto.Group] : [],
    status: (dto.Status ?? []).map((badge) => ({
      label: badge.Status ?? '',
      color: BADGE_TONES[String(badge.Color).toLowerCase()] ?? 'dark',
    })),

    /* THE ROW IS ALSO THE EDIT FORM'S SOURCE. No column shows any of this, and
       nothing else in the slice hands out a profile — `/api/user-details` stops
       at the name, the email and the attributes. The legacy form is filled the
       same way, off the same row.

       Carrying them matters beyond a filled form: `UpdateProfile` assigns every
       one of these columns unconditionally, so anything the form cannot send
       back is written to NULL on the next save. */
    nameAr: dto.ArabicName ?? '',
    nameEn: dto.EnglishName ?? '',
    groupId: dto.GroupId ?? null,
    gender: dto.Gender ?? null,
    /* `Profiles.Phone` on the way in, `Phone` on the way back out — the row and
       the update request name the same column differently. */
    phone: dto.TelephoneNumber ?? '',
    mobile: dto.Mobile ?? '',
    employeeId: dto.EmployeeId ?? '',
    attributes: [
      dto.Attribute1 ?? '',
      dto.Attribute2 ?? '',
      dto.Attribute3 ?? '',
      dto.Attribute4 ?? '',
      dto.Attribute5 ?? '',
    ],
  }
}

/** The column order the endpoint sorts by index against. */
const COLUMNS = ['name', 'userName', 'email', 'groups', 'status']

/**
 * @param {object} [options]
 * @param {string} [options.type]      one of `UsersQueryTypes` — the tab
 * @param {number|null} [options.groupId]
 * @param {boolean|null} [options.isAdmin]
 * @param {number} [options.page]      1-based
 * @param {number} [options.pageSize]
 * @param {string} [options.search]
 * @param {{key: string, dir: 'asc'|'desc'}} [options.sort]
 */
export async function listUsers({
  type = 'ACTIVE',
  groupId = null,
  isAdmin = null,
  page = 1,
  pageSize = 10,
  search = '',
  sort,
  signal,
} = {}) {
  const response = await api.post(
    '/Users/Filter',
    {
      DataParams: dataParams({ page, pageSize, search, sort, columns: COLUMNS }),
      Type: type,
      GroupId: groupId,
      IsAdmin: isAdmin,
    },
    { signal },
  )

  const { rows, total, totalUnfiltered } = readDataTable(response)
  return { rows: rows.map(toRow), total, totalUnfiltered }
}

/** Departments and the other page-load data, in one call. */
export async function getUsersPage(signal) {
  const dto = (await api.get('/api/users', { signal })) ?? {}
  /* `ArabicName`/`EnglishName` — this is `GroupsManagement.Common.GroupDto`, which the
     users page reuses. The entity underneath calls them `NameArabic`/`NameEnglish`, and
     reading THOSE off the DTO yields undefined: a dropdown of blank rows, no error. */
  const label = (group) => (isEnglish ? group.EnglishName : group.ArabicName) || group.ArabicName || ''

  return {
    groups: (dto.Groups ?? []).map((group) => ({
      value: group.GroupId,
      label: label(group),
      typeId: group.TypeId,
    })),
    privateGroups: (dto.PrivateGroups ?? []).map((group) => ({
      value: group.GroupId,
      label: label(group),
      typeId: group.TypeId,
    })),
    companies: (dto.Companies ?? []).map((company) => ({
      id: company.CompanyId,
      name: company.Name ?? '',
    })),
    roles: (dto.Roles ?? []).map((role, index) => ({
      id: dto.RolesIds?.[index] ?? role,
      name: role,
    })),
    /* Whether the "clone from directory" and "fetch from data source" actions can work
       at all — both need a configured directory. */
    hasUsersDataSource: Boolean(dto.UsersIntegrationDataSourceExist),
    hasFetchDataSource: Boolean(dto.UserFetchInfoDataSourceExist),
    dataSources: (dto.DataSources ?? []).map((source) => ({
      id: source.Id,
      name: (isEnglish ? source.EnglishName : source.ArabicName) || '',
    })),
  }
}

/** Departments then custom groups, which is the order the legacy filter select uses. */
/** Departments first, then the rest, each block alphabetical. */
export const sortGroups = (groups) =>
  [...groups].sort((a, b) => a.typeId - b.typeId || a.label.localeCompare(b.label, 'ar'))

export async function listGroupOptions(signal) {
  return sortGroups((await getUsersPage(signal)).groups)
}

export async function listCompanies(signal) {
  return (await getUsersPage(signal)).companies
}

/**
 * The export.
 *
 * NO CALLER TODAY. The users screen's export menu was removed on request, and this is
 * kept rather than deleted because the endpoint is live and unchanged — the control can
 * come back without re-deriving the query. Nothing else imports it.
 *
 * Fetched and saved rather than opened in a tab (W4): the request carries the session
 * cookie either way, but this keeps the failure — an expired session, a refused role — on
 * the screen the user is looking at instead of as an error page in a new tab.
 */
export async function exportUsers({ type, format, filter = '' }) {
  const query = new URLSearchParams({ tableType: format, type, filter })

  try {
    const saved = await download(`/Users/Export?${query}`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* ---- the actions -------------------------------------------------------
   Every one binds `[FromBody]` and answers `{ Result, Message }`, which
   `src/lib/api.js` raises as an ApiError when `Result` is false. `act()` turns
   that back into an outcome the screen can show either way, so no caller writes
   its own try/catch and no failure is reported as a success. */

async function act(path, body) {
  try {
    const data = await api.post(path, body)
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export function createUser(values) {
  return act('/Users/CreateNewUser', {
    Username: values.username ?? '',
    Password: values.password ?? '',
    Email: values.email ?? '',
    ArabicFullName: values.arabicName ?? '',
    EnglishFullName: values.englishName ?? '',
    GroupId: values.groupId ? Number(values.groupId) : null,
    SelectedCompanyId: values.companyId ? Number(values.companyId) : null,
    WorkingPeriod: values.workingPeriod ? Number(values.workingPeriod) : null,
    SendActivationEmail: values.sendActivationEmail !== false,
  })
}

/**
 * The edit form, saved.
 *
 * IT IS A FULL OVERWRITE, not a patch. `UpdateProfileHandler` assigns all twelve
 * columns unconditionally, so every field the form does not send is written to
 * NULL — which is why the attributes are carried through the form untouched
 * rather than left out of the request.
 *
 * `Department` is the department's ID as a string: the handler parses it with
 * `int.TryParse` and refuses the whole save on failure. Sending the department's
 * NAME there is a rejected save, not a mis-assignment.
 *
 * @param {object} values  the drawer's own shape — `nameAr`, `nameEn`, `group`, …
 */
export function updateProfile(values) {
  const attributes = values.attributes ?? []

  return act('/Users/UpdateProfile', {
    Username: values.userName ?? '',
    ArabicName: values.nameAr ?? '',
    EnglishName: values.nameEn ?? '',
    Department: values.group ?? '',
    Gender: values.gender === '' || values.gender == null ? null : Number(values.gender),
    Email: values.email ?? '',
    Phone: values.phone ?? '',
    Mobile: values.mobile ?? '',
    EmployeeId: values.employeeId ?? '',
    Attribute1: attributes[0] ?? '',
    Attribute2: attributes[1] ?? '',
    Attribute3: attributes[2] ?? '',
    Attribute4: attributes[3] ?? '',
    Attribute5: attributes[4] ?? '',
  })
}

export const deleteUser = (username) => act('/Users/DeleteUser', { Username: username })

/** A null date CLEARS the limit, which is how "no expiry" is expressed. */
export const setUserExpiry = (username, activeUntil) =>
  act('/Users/SetUserExpiry', { Username: username, WorkingPeriod: activeUntil || null })

export const setUserLocking = (username, until) =>
  act('/Users/SetUserLocking', { Username: username, Date: until || null })

export const setUserResignation = (username, date) =>
  act('/Users/SetUserResignation', { Username: username, Date: date || null })

export const setUserApproved = (username, isApproved, notes) =>
  act('/Users/SetUserApprovedStatus', {
    Username: username,
    IsApproved: Boolean(isApproved),
    Notes: notes ?? '',
  })

export const setUserUnlocked = (username) =>
  act('/Users/SetUserUnlockedStatus', { Username: username })

export const changePassword = (username, password) =>
  act('/Users/ChangePassword', { Username: username, Password: password })

/** Sends the user a reset link; it does not choose a password for them. */
export const resetPassword = (username) => act('/Users/ResetPassword', { Username: username })

export const updateUserRoles = (username, roles) =>
  act('/Users/UpdateUserRoles', { Username: username, Roles: roles ?? [] })

/** The roles a user currently holds, for the edit drawer's roles tab. */
export async function getUserRoles(username, signal) {
  const data = await api.post('/Users/GetUserRole', { Username: username }, { signal })
  return Array.isArray(data) ? data : (data?.Roles ?? [])
}

/** Bulk delete. The endpoint takes the list, so the confirmation must name what it has. */
export const deleteInactiveUsers = (usernames) =>
  act('/Users/DeleteInActiveUsers', { SelectedUsers: usernames ?? [] })

export const cloneUsersFromDirectory = (activeDirectoryId) =>
  act('/Users/Clone', { ActiveDirectoryId: Number(activeDirectoryId) })

/**
 * One user's details, read out of the configured directory.
 *
 * NOT an action, despite having been written as one: it answers `{ Result, Message, Data }`
 * where `Data` is the whole AD profile, and `act` kept the first two and threw the third
 * away. This is what fills the create form from the directory rather than by hand.
 *
 * The field names are the AD service's, not the users slice's — `ArabicName`, not
 * `NameArabic`; `GroupArabicName` for the department, which is a NAME and not the group id
 * the form's select needs, so it is returned for the caller to match on.
 */
export async function getUserInfoFromDataSource(username, sourceId) {
  try {
    const data = await api.post('/Users/GetUserInfoFromDataSource', {
      Username: username,
      SourceId: Number(sourceId),
    })

    if (!data || data.Result === false || !data.Data) {
      return { ok: false, message: data?.Message ?? '', profile: null }
    }

    const dto = data.Data
    return {
      ok: true,
      message: data.Message ?? '',
      profile: {
        userName: dto.Username ?? '',
        email: dto.Email ?? '',
        nameAr: dto.ArabicName ?? '',
        nameEn: dto.EnglishName ?? '',
        groupNameAr: dto.GroupArabicName ?? '',
        groupNameEn: dto.GroupEnglishName ?? '',
        phone: dto.Phone ?? '',
        mobile: dto.Mobile ?? '',
        employeeId: dto.EmployeeId ?? '',
        gender: dto.Gender ?? '',
        attributes: [
          dto.Attribute1 ?? '',
          dto.Attribute2 ?? '',
          dto.Attribute3 ?? '',
          dto.Attribute4 ?? '',
          dto.Attribute5 ?? '',
        ],
      },
    }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, profile: null }
    throw error
  }
}

/**
 * The bulk import.
 *
 * `CreateMultiUsers` is the endpoint the `finishUpload` convention was written for: it
 * answers `text/html` containing `window.parent.finishUpload(bool,'…')` rather than JSON.
 * `upload.js` parses that reply without executing it, so this reads like every other
 * action — and reports the server's own summary, which lists the rows it could not take.
 *
 * @param {File} file
 * @param {string} fileType  which import shape the file is — the endpoint branches on it
 */
/**
 * A row of the TEMPLATE, as the row `CreateMultiUsersHandler` reads.
 *
 * The two disagree, and both halves of the disagreement are silent — see
 * `API_FINDINGS.md`. `Users_UsersByEmail.xlsx` is headed
 * `Email | FullName | Department` while the handler reads column 2 as the ARABIC name and
 * column 3 as the ENGLISH one, so an import wrote the department into the English name and
 * assigned nobody to anything. `Users_ActiveDirectoryUsers.xlsx` is
 * `Username | Email | Department Name | Full Name` against the same mistake.
 *
 * The legacy is unambiguous (`UserProfileManager.CreateLocalUsersByEmail`, and its AD
 * sibling): ONE full name, stored in both language columns, and a department by name.
 * That is what these produce — except the department, which is dropped, because no
 * endpoint in this slice does anything with it.
 */
const IMPORT_COLUMNS = {
  // Email | FullName | Department  ->  email, arabicName, englishName
  UsersByEmail: (row) => [row[0], row[1], row[1]],
  // Username | Email | Department | Full Name  ->  username, email, arabicName, englishName
  ActiveDirectoryUsers: (row) => [row[0], row[1], row[3], row[3]],
  // Username | Resignation Date. `readWorkbookRows` has already made a real date cell ISO,
  // which is the one format the handler's InvariantCulture parse reads the same way the
  // template's own `d/M/yyyy` is written.
  ResignedUsers: (row) => [row[0], row[1]],
  ResetUsersPasswords: (row) => [row[0]],
  Users: (row) => row.slice(0, 4),
}

export async function importUsers({ file, fileType, companyId }) {
  if (!file) return { ok: false, message: '' }

  /* Converted in the browser: the endpoint cannot read a workbook at all, and its CSV
     path is correct — see `src/lib/spreadsheet.js`. A file already in CSV is sent as it
     came, unmapped, because its columns are whatever the person typed. */
  const payload = await workbookToCsv(file, IMPORT_COLUMNS[fileType])

  const form = new FormData()
  form.append('csv', payload)
  form.append('fileType', fileType ?? '')
  if (companyId) form.append('selectedCompanyId', String(companyId))

  try {
    const data = await upload('/Users/CreateMultiUsers', form)
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export { groupTypes, locations, roles } from '../../data/directory'
